// Unified Storage Adapter for Desktop (Electron) and Mobile (Capacitor)
// Provides consistent API regardless of platform
import { Capacitor } from '@capacitor/core';

const formatStorageError = (error) => {
    if (!error) return 'Unknown error';
    if (typeof error === 'string') return error;
    if (error?.message) return String(error.message);
    if (error?.error?.message) return String(error.error.message);
    try {
        return JSON.stringify(error);
    } catch (_) {
        return String(error);
    }
};

/**
 * Storage Adapter Class
 * Automatically detects platform and uses appropriate storage backend
 */
class StorageAdapter {
    constructor() {
        this.platform = this.detectPlatform();
        this.db = null;
        this.dbConnection = null; // For Capacitor SQLite connection
        // Default sandbox name; Android overrides with absolute Lytsoft path
        this.dbName = 'restaurant-pos-offline';
        this.initPromise = null; // Single-flight guard for concurrent init calls
    }

    /**
     * Detect the current platform
     * @returns {string} 'electron' | 'mobile' | 'indexeddb'
     */
    detectPlatform() {
        // 1. Electron - Trusted UserAgent check
        if (typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes(' electron/')) {
            return 'electron';
        }

        // 2. Mobile (Native) - Trusted Capacitor check
        if (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform()) {
            return 'mobile';
        }

        // 3. Fallback - Web/Dev
        return 'indexeddb';
    }

    /**
     * Initialize Capacitor SQLite database
     * @returns {Promise<void>}
     */
    async initMobileSQLite() {
        try {
            if (this.db && this.dbConnection) {
                return;
            }

            // Check if we're actually on a native platform
            if (!Capacitor.isNativePlatform()) {
                throw new Error('Not running on a native platform. SQLite is only available on Android/iOS.');
            }


            // Wait for Capacitor to be fully ready before accessing plugins
            // This is critical - plugins may not be available immediately on app start
            await new Promise((resolve) => {
                if (document.readyState === 'complete') {
                    resolve();
                } else {
                    window.addEventListener('load', resolve, { once: true });
                }
            });

            // Wait for native bridge to be fully initialized with retry
            // 100ms is insufficient on first install / cold start — use progressive delay
            const bridgeDelays = [200, 300, 500];
            for (let attempt = 0; attempt < bridgeDelays.length; attempt++) {
                await new Promise(resolve => setTimeout(resolve, bridgeDelays[attempt]));
                // Check if bridge is ready by testing Capacitor.Plugins availability
                if (window.Capacitor?.Plugins) break;
            }

            // Android: open SQLite under public Lytsoft/ (survives Clear data).
            // Absolute connection name → native opens …/restaurant-pos-offlineSQLite.db
            if (Capacitor.getPlatform() === 'android') {
                const { ensureAndroidLytsoftDb } = await import('./lytsoftDataDir');
                const lyt = await ensureAndroidLytsoftDb();
                this.dbName = lyt.dbConnectionName;
                console.log('[StorageAdapter] Android Lytsoft dbName=', this.dbName);
            }

            // Dynamic import to avoid issues when plugin is not installed
            const sqliteModule = await import('@capacitor-community/sqlite');

            let { CapacitorSQLite, SQLiteConnection } = sqliteModule;

            // Check if CapacitorSQLite plugin is available

            // Try alternative access method via Capacitor.Plugins if module import fails
            if (!CapacitorSQLite && window.Capacitor?.Plugins?.CapacitorSQLite) {
                CapacitorSQLite = window.Capacitor.Plugins.CapacitorSQLite;
            }

            if (!CapacitorSQLite) {
                console.error('[StorageAdapter] CapacitorSQLite is null/undefined');
                console.error('[StorageAdapter] Module keys:', Object.keys(sqliteModule));
                console.error('[StorageAdapter] Available on window.Capacitor.Plugins:',
                    window.Capacitor?.Plugins ? Object.keys(window.Capacitor.Plugins) : 'window.Capacitor.Plugins not available');
                throw new Error('CapacitorSQLite plugin is not available. The native plugin may not be properly registered.');
            }

            if (!SQLiteConnection) {
                console.error('[StorageAdapter] SQLiteConnection is null/undefined');
                throw new Error('SQLiteConnection class is not available from @capacitor-community/sqlite');
            }

            const sqlite = new SQLiteConnection(CapacitorSQLite);

            // First attempt to retrieve an existing connection.
            // On Android hot-reloads, plugin state can outlive JS context.
            let hasRetrievedExistingConnection = false;
            try {
                this.dbConnection = await sqlite.retrieveConnection(this.dbName, false);
                hasRetrievedExistingConnection = !!this.dbConnection;
            } catch (_) {
                hasRetrievedExistingConnection = false;
            }

            if (!hasRetrievedExistingConnection) {
                try {
                    // Create and open database connection
                    this.dbConnection = await sqlite.createConnection(
                        this.dbName,
                        false, // encrypted
                        'no-encryption', // mode
                        1 // version
                    );
                } catch (createErr) {
                    const createErrMsg = String(createErr?.message || createErr || '').toLowerCase();
                    if (createErrMsg.includes('already exists')) {
                        // Another concurrent caller created it first.
                        this.dbConnection = await sqlite.retrieveConnection(this.dbName, false);
                    } else {
                        throw createErr;
                    }
                }

                try {
                    await this.dbConnection.open();
                } catch (openErr) {
                    const openErrMsg = String(openErr?.message || openErr || '').toLowerCase();
                    if (!openErrMsg.includes('already open')) {
                        throw openErr;
                    }
                }

                // Create all tables
                await this.createMobileTables();

            } else {
                try {
                    await this.dbConnection.open();
                } catch (openErr) {
                    const openErrMsg = String(openErr?.message || openErr || '').toLowerCase();
                    if (!openErrMsg.includes('already open')) {
                        throw openErr;
                    }
                }
            }

            // Store reference with methods that match Electron API pattern
            this.db = {
                query: async (sql, params = []) => {
                    try {
                        const result = await this.dbConnection.query(sql, params);
                        return { ok: true, data: result.values || [] };
                    } catch (error) {
                        return { ok: false, error: formatStorageError(error) };
                    }
                },
                run: async (sql, params = []) => {
                    try {
                        const result = await this.dbConnection.run(sql, params, false);
                        return {
                            ok: true,
                            changes: result.changes?.changes || 0,
                            lastInsertRowid: result.changes?.lastId || 0
                        };
                    } catch (error) {
                        return { ok: false, error: formatStorageError(error) };
                    }
                },
                get: async (sql, params = []) => {
                    try {
                        const result = await this.dbConnection.query(sql, params);
                        return {
                            ok: true,
                            data: result.values && result.values.length > 0 ? result.values[0] : undefined
                        };
                    } catch (error) {
                        return { ok: false, error: formatStorageError(error) };
                    }
                },
                execute: async (sql) => {
                    try {
                        await this.dbConnection.execute(sql, false);
                        return { ok: true };
                    } catch (error) {
                        return { ok: false, error: formatStorageError(error) };
                    }
                }
            };
        } catch (error) {
            console.error('[StorageAdapter] Failed to initialize Capacitor SQLite:', error);
            // Graceful fallback: use IndexedDB instead of crashing
            console.warn('[StorageAdapter] Falling back to IndexedDB on mobile due to SQLite init failure');
            this.platform = 'indexeddb';
            try {
                await this.initIndexedDB();
            } catch (idbError) {
                console.error('[StorageAdapter] IndexedDB fallback also failed:', idbError);
                throw new Error(`Mobile storage initialization failed: SQLite: ${formatStorageError(error)}, IndexedDB: ${formatStorageError(idbError)}`);
            }
        }
    }

    /**
     * Create database tables for Capacitor SQLite
     * @returns {Promise<void>}
     */
    async createMobileTables() {
        const createTableStatements = [
            // Main key-value store table
            `CREATE TABLE IF NOT EXISTS main_store(
    key TEXT PRIMARY KEY,
    value TEXT NOT NULL,
    created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
    updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
)`,

            // Admin users table
            `CREATE TABLE IF NOT EXISTS admin_users(
    key TEXT PRIMARY KEY,
    value TEXT NOT NULL,
    created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
    updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
)`,

            // Sessions table
            `CREATE TABLE IF NOT EXISTS sessions(
    key TEXT PRIMARY KEY,
    value TEXT NOT NULL,
    created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
    updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
)`,

            // Outbox sales: composite PK so each store can use offline-{pc}-{n}
            `CREATE TABLE IF NOT EXISTS outbox_sales(
    sale_id TEXT NOT NULL,
    date TEXT NOT NULL,
    status TEXT NOT NULL,
    upload_response TEXT,
    upload_date TEXT,
    payload TEXT NOT NULL,
    upload_invoice TEXT,
    attempts INTEGER DEFAULT 0,
    last_attempt_date TEXT,
    store_db TEXT NOT NULL DEFAULT '',
    client_meta TEXT,
    created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
    updated_at DATETIME DEFAULT CURRENT_TIMESTAMP,
    PRIMARY KEY (sale_id, store_db)
)`,

            // Outbox voids table
            `CREATE TABLE IF NOT EXISTS outbox_voids(
    void_id TEXT PRIMARY KEY,
    date TEXT NOT NULL,
    status TEXT NOT NULL,
    payload TEXT NOT NULL,
    attempts INTEGER DEFAULT 0,
    last_attempt_date TEXT,
    created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
    updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
)`,

            // Outbox logins table
            `CREATE TABLE IF NOT EXISTS outbox_logins(
    id TEXT PRIMARY KEY,
    username TEXT NOT NULL,
    platform TEXT NOT NULL,
    pc TEXT NOT NULL,
    store_db TEXT NOT NULL,
    date TEXT NOT NULL,
    status TEXT NOT NULL,
    attempts INTEGER DEFAULT 0,
    last_attempt_date TEXT,
    synced_at TEXT,
    created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
    updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
)`,

            // Offline tables cache used by table-server mode (Android host/client parity)
            `CREATE TABLE IF NOT EXISTS offline_tables(
    sale_number TEXT NOT NULL,
    store_db TEXT NOT NULL,
    table_data TEXT NOT NULL,
    user TEXT,
    pc TEXT,
    created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
    updated_at DATETIME DEFAULT CURRENT_TIMESTAMP,
    PRIMARY KEY (sale_number, store_db)
)`,

            // Pending table operations queue used by local table server sync
            `CREATE TABLE IF NOT EXISTS outbox_table_ops(
    id TEXT PRIMARY KEY,
    type TEXT NOT NULL,
    payload TEXT,
    sale_number TEXT,
    store_db TEXT,
    status TEXT DEFAULT 'pending',
    created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
    attempts INTEGER DEFAULT 0,
    last_error TEXT,
    updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
)`,

            // Invoice number counter table
            `CREATE TABLE IF NOT EXISTS invoice_numbers(
    key TEXT PRIMARY KEY,
    value INTEGER NOT NULL,
    updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
)`,

            // SQL-first stock cache table for large catalogs
            `CREATE TABLE IF NOT EXISTS stock_items(
    plu TEXT PRIMARY KEY,
    barcode TEXT,
    description TEXT NOT NULL,
    description2 TEXT,
    department TEXT,
    sub_dep TEXT,
    sub_dep2 TEXT,
    fast_line INTEGER DEFAULT 0,
    archived INTEGER DEFAULT 0,
    has_image INTEGER DEFAULT 0,
    updated_at TEXT NOT NULL,
    data_json TEXT NOT NULL
)`,

            // Indexes
            `CREATE INDEX IF NOT EXISTS idx_outbox_sales_status ON outbox_sales(status)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_sales_date ON outbox_sales(date)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_sales_store_db ON outbox_sales(store_db)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_voids_status ON outbox_voids(status)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_logins_status ON outbox_logins(status)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_logins_date ON outbox_logins(date)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_logins_store_db ON outbox_logins(store_db)`,
            `CREATE INDEX IF NOT EXISTS idx_offline_tables_user ON offline_tables(user)`,
            `CREATE INDEX IF NOT EXISTS idx_offline_tables_store_db ON offline_tables(store_db)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_table_ops_status ON outbox_table_ops(status)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_table_ops_store_db ON outbox_table_ops(store_db)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_table_ops_created_at ON outbox_table_ops(created_at)`,
            `CREATE INDEX IF NOT EXISTS idx_stock_barcode ON stock_items(barcode)`,
            `CREATE INDEX IF NOT EXISTS idx_stock_department ON stock_items(department, sub_dep, sub_dep2)`,
            `CREATE INDEX IF NOT EXISTS idx_stock_updated_at ON stock_items(updated_at)`,
            `CREATE INDEX IF NOT EXISTS idx_stock_fast_line ON stock_items(fast_line, archived)`
        ];

        for (const sql of createTableStatements) {
            try {
                await this.dbConnection.execute(sql, false);
            } catch (error) {
                console.error(`[StorageAdapter] Error creating table: ${sql.substring(0, 50)}...`, error);
                throw error;
            }
        }

        await this.migrateOutboxSalesCompositePkMobile();
    }

    /**
     * Mobile: migrate outbox_sales to PRIMARY KEY (sale_id, store_db).
     */
    async migrateOutboxSalesCompositePkMobile() {
        try {
            const info = await this.dbConnection.query('PRAGMA table_info(outbox_sales)');
            const cols = Array.isArray(info?.values)
                ? info.values.map((row) => {
                    if (Array.isArray(row)) {
                        return { name: row[1], pk: row[5] };
                    }
                    return { name: row.name, pk: row.pk };
                })
                : [];
            if (cols.length === 0) return;

            const pkCols = cols
                .filter((c) => Number(c.pk) > 0)
                .sort((a, b) => Number(a.pk) - Number(b.pk))
                .map((c) => c.name);

            const isComposite =
                pkCols.length === 2 &&
                pkCols[0] === 'sale_id' &&
                pkCols[1] === 'store_db';
            if (isComposite) return;

            console.log('[StorageAdapter] Migrating mobile outbox_sales to composite PK');
            const steps = [
                `CREATE TABLE IF NOT EXISTS outbox_sales_v2 (
                    sale_id TEXT NOT NULL,
                    date TEXT NOT NULL,
                    status TEXT NOT NULL,
                    upload_response TEXT,
                    upload_date TEXT,
                    payload TEXT NOT NULL,
                    upload_invoice TEXT,
                    attempts INTEGER DEFAULT 0,
                    last_attempt_date TEXT,
                    store_db TEXT NOT NULL DEFAULT '',
                    client_meta TEXT,
                    created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
                    updated_at DATETIME DEFAULT CURRENT_TIMESTAMP,
                    PRIMARY KEY (sale_id, store_db)
                )`,
                `INSERT OR IGNORE INTO outbox_sales_v2
                    (sale_id, date, status, upload_response, upload_date, payload, upload_invoice,
                     attempts, last_attempt_date, store_db, client_meta, created_at, updated_at)
                 SELECT
                    sale_id, date, status, upload_response, upload_date, payload, upload_invoice,
                    attempts, last_attempt_date, COALESCE(store_db, ''), client_meta, created_at, updated_at
                 FROM outbox_sales`,
                `DROP TABLE outbox_sales`,
                `ALTER TABLE outbox_sales_v2 RENAME TO outbox_sales`,
                `CREATE INDEX IF NOT EXISTS idx_outbox_sales_status ON outbox_sales(status)`,
                `CREATE INDEX IF NOT EXISTS idx_outbox_sales_date ON outbox_sales(date)`,
                `CREATE INDEX IF NOT EXISTS idx_outbox_sales_store_db ON outbox_sales(store_db)`
            ];
            for (const sql of steps) {
                await this.dbConnection.execute(sql, false);
            }
            console.log('[StorageAdapter] Mobile outbox_sales composite PK migration done');
        } catch (e) {
            console.error('[StorageAdapter] Mobile outbox_sales migration failed:', e);
        }
    }

    /**
     * Wait for window.database to be ready (Electron only)
     * Tests actual database readiness, not just API availability
     * @returns {Promise<boolean>}
     */
    async waitForDatabase() {
        const maxAttempts = 200; // 20 seconds max
        const delay = 100; // 100ms between attempts
        let lastError = null;

        for (let i = 0; i < maxAttempts; i++) {
            const databaseApi = window?.database;
            if (!databaseApi) {
                // API not exposed yet, wait
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
            }

            const hasCoreMethods =
                typeof databaseApi.query === 'function' &&
                typeof databaseApi.run === 'function' &&
                typeof databaseApi.get === 'function';

            if (!hasCoreMethods) {
                lastError = new Error('window.database is missing required query/run/get methods');
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
            }

            // Preferred readiness probe for modern preload bridge
            try {
                if (typeof databaseApi.health === 'function') {
                    const result = await databaseApi.health();

                    if (result === true || (result && result.ok === true)) {
                        return true;
                    }

                    if (result && result.ok === false && i % 10 === 0) {
                        console.log(`[StorageAdapter] Database initializing (attempt ${i + 1})...`, result.error || '');
                    }
                }
            } catch (error) {
                lastError = error;
                if (i % 10 === 0) {
                    console.log(`[StorageAdapter] Waiting for database (attempt ${i + 1})...`);
                }
            }

            // Compatibility probe for bridges without health()
            try {
                const probe = await databaseApi.query('SELECT 1 as test', []);
                if (probe && probe.ok === true) {
                    return true;
                }
            } catch (error) {
                lastError = error;
            }

            await new Promise(resolve => setTimeout(resolve, delay));
        }

        console.error('[StorageAdapter] Timeout waiting for database initialization', lastError);
        return false;
    }

    /**
     * Initialize storage
     */
    async init() {
        if (this.db) return; // Already initialized
        if (this.initPromise) {
            await this.initPromise;
            return;
        }

        this.initPromise = (async () => {
        switch (this.platform) {
            case 'electron': {
                // Wait for the Electron database bridge, but keep the app functional if it never comes up.
                const isReady = await this.waitForDatabase();
                if (isReady) {
                    this.db = window.database;
                } else {
                    console.warn('[StorageAdapter] Falling back to IndexedDB: Electron database bridge unavailable');
                    this.platform = 'indexeddb';
                    await this.initIndexedDB();
                }
                break;
            }

            case 'mobile': {
                // Use SQLite on Capacitor/mobile with retry for cold-start race condition
                const maxRetries = 2;
                for (let retry = 0; retry <= maxRetries; retry++) {
                    try {
                        await this.initMobileSQLite();
                        break;
                    } catch (err) {
                        if (retry < maxRetries) {
                            console.warn(`[StorageAdapter] Mobile SQLite init attempt ${retry + 1} failed, retrying...`, err.message);
                            await new Promise(resolve => setTimeout(resolve, 500 * (retry + 1)));
                            // Reset state for retry
                            this.db = null;
                            this.dbConnection = null;
                        }
                        // On final failure, initMobileSQLite's own catch will fallback to IndexedDB
                    }
                }
                break;
            }

            case 'indexeddb':
                // IndexedDB (web/development)
                await this.initIndexedDB();
                break;
        }
        })();

        try {
            await this.initPromise;
        } finally {
            this.initPromise = null;
        }
    }

    /**
     * Execute a SELECT query
     * @param {string} sql SQL query
     * @param {Array} params Query parameters
     * @returns {Promise<Array>} Query results
     */
    async query(sql, params = []) {
        await this.init();

        switch (this.platform) {
            case 'electron': {
                const result = await this.db.query(sql, params);
                if (!result.ok) {
                    throw new Error(result.error);
                }
                return result.data;
            }

            case 'mobile': {
                const result = await this.db.query(sql, params);
                if (!result.ok) {
                    throw new Error(result.error || 'Query failed');
                }
                return result.data;
            }

            case 'indexeddb':
                // Map SQL to IndexedDB operations
                return this.executeIndexedDB(sql, params);
        }
    }

    /**
     * Execute an INSERT/UPDATE/DELETE statement
     * @param {string} sql SQL statement
     * @param {Array} params Statement parameters
     * @returns {Promise<Object>} Execution result with changes and lastInsertRowid
     */
    async run(sql, params = []) {
        await this.init();

        switch (this.platform) {
            case 'electron': {
                const result = await this.db.run(sql, params);
                if (!result.ok) {
                    throw new Error(result.error);
                }
                return { changes: result.changes, lastInsertRowid: result.lastInsertRowid };
            }

            case 'mobile': {
                const result = await this.db.run(sql, params);
                if (!result.ok) {
                    throw new Error(result.error || 'Run failed');
                }
                return { changes: result.changes, lastInsertRowid: result.lastInsertRowid };
            }

            case 'indexeddb':
                // Map SQL to IndexedDB operations
                return this.executeIndexedDB(sql, params);
        }
    }

    /**
     * Get a single row
     * @param {string} sql SQL query
     * @param {Array} params Query parameters
     * @returns {Promise<Object|undefined>} Single row or undefined
     */
    async get(sql, params = []) {
        await this.init();

        switch (this.platform) {
            case 'electron': {
                const result = await this.db.get(sql, params);
                if (!result.ok) {
                    throw new Error(result.error);
                }
                return result.data;
            }

            case 'mobile': {
                const result = await this.db.get(sql, params);
                if (!result.ok) {
                    throw new Error(result.error || 'Get failed');
                }
                return result.data;
            }

            case 'indexeddb':
                // Map SQL to IndexedDB operations
                return this.executeIndexedDB(sql, params);
        }
    }

    /**
     * Get value by key from a specific store (Storage.js compatibility)
     * @param {string} key Key to retrieve
     * @param {string} storeName Store name (main_store, admin_users, sessions)
     * @returns {Promise<any>} Value or undefined
     */
    async getValue(key, storeName = 'main_store') {
        await this.init();

        switch (this.platform) {
            case 'electron': {
                const result = await this.db.get(
                    `SELECT value FROM ${storeName} WHERE key = ? `,
                    [key]
                );
                if (!result.ok) {
                    throw new Error(result.error);
                }

                if (!result.data) {
                    return undefined;
                }

                const value = result.data.value;

                // Try to parse JSON strings back to objects
                if (typeof value === 'string') {
                    // Check if it looks like JSON (starts with { or [)
                    if ((value.startsWith('{') && value.endsWith('}')) ||
                        (value.startsWith('[') && value.endsWith(']'))) {
                        try {
                            return JSON.parse(value);
                        } catch (e) {
                            // Not valid JSON, return as-is
                            return value;
                        }
                    }
                }

                return value;
            }

            case 'mobile': {
                const result = await this.db.get(
                    `SELECT value FROM ${storeName} WHERE key = ? `,
                    [key]
                );
                if (!result.ok) {
                    throw new Error(result.error || 'Get failed');
                }

                if (!result.data) {
                    return undefined;
                }

                const value = result.data.value;

                // Try to parse JSON strings back to objects
                if (typeof value === 'string') {
                    // Check if it looks like JSON (starts with { or [)
                    if ((value.startsWith('{') && value.endsWith('}')) ||
                        (value.startsWith('[') && value.endsWith(']'))) {
                        try {
                            return JSON.parse(value);
                        } catch (e) {
                            // Not valid JSON, return as-is
                            return value;
                        }
                    }
                }

                return value;
            }

            case 'indexeddb': {
                // Map store names to IndexedDB object stores
                const idbStoreName = this.mapStoreNameToIDB(storeName);
                const tx = this.db.transaction(idbStoreName, 'readonly');
                const store = tx.objectStore(idbStoreName);
                return await store.get(key);
            }
        }
    }

    /**
     * Set value by key in a specific store (Storage.js compatibility)
     * @param {string} key Key to set
     * @param {any} value Value to store
     * @param {string} storeName Store name (main_store, admin_users, sessions)
     * @returns {Promise<void>}
     */
    async setValue(key, value, storeName = 'main_store') {
        await this.init();

        // Treat null/undefined writes as a delete. SQLite columns with a
        // NOT NULL constraint (e.g. main_store.value) would reject an
        // INSERT/UPDATE with a null value, so route deletes through DELETE
        // instead. Mirrors the Retail POS fix applied 2026-06-24.
        if (value === null || value === undefined) {
            switch (this.platform) {
                case 'electron': {
                    const result = await this.db.run(
                        `DELETE FROM ${storeName} WHERE key = ?`,
                        [key]
                    );
                    if (!result.ok) {
                        throw new Error(result.error || 'DeleteValue failed');
                    }
                    return;
                }
                case 'mobile': {
                    const result = await this.db.run(
                        `DELETE FROM ${storeName} WHERE key = ?`,
                        [key]
                    );
                    if (!result.ok) {
                        throw new Error(result.error || 'DeleteValue failed');
                    }
                    return;
                }
                case 'indexeddb': {
                    const idbStoreName = this.mapStoreNameToIDB(storeName);
                    const tx = this.db.transaction(idbStoreName, 'readwrite');
                    const store = tx.objectStore(idbStoreName);
                    await store.delete(key);
                    return;
                }
                case 'memory': {
                    this.memoryStore[storeName].delete(key);
                    return;
                }
                default:
                    throw new Error(`Unknown platform: ${this.platform}`);
            }
        }

        switch (this.platform) {
            case 'electron': {
                // SQLite only accepts primitives (numbers, strings, bigints, buffers)
                // Serialize complex objects to JSON strings
                let serializedValue = value;
                if (value !== null && value !== undefined && typeof value === 'object') {
                    serializedValue = JSON.stringify(value);
                } else if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'bigint' && value !== null) {
                    serializedValue = String(value);
                }

                const result = await this.db.run(
                    `INSERT INTO ${storeName} (key, value, updated_at)
VALUES(?, ?, CURRENT_TIMESTAMP)
                     ON CONFLICT(key) DO UPDATE SET value = ?, updated_at = CURRENT_TIMESTAMP`,
                    [key, serializedValue, serializedValue]
                );
                if (!result.ok) {
                    throw new Error(result.error);
                }
                break;
            }

            case 'mobile': {
                // SQLite only accepts primitives (numbers, strings, bigints, buffers)
                // Serialize complex objects to JSON strings
                let serializedValue = value;
                if (value !== null && value !== undefined && typeof value === 'object') {
                    serializedValue = JSON.stringify(value);
                } else if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'bigint' && value !== null) {
                    serializedValue = String(value);
                }

                const result = await this.db.run(
                    `INSERT INTO ${storeName} (key, value, updated_at)
VALUES(?, ?, datetime('now'))
                     ON CONFLICT(key) DO UPDATE SET value = ?, updated_at = datetime('now')`,
                    [key, serializedValue, serializedValue]
                );
                if (!result.ok) {
                    throw new Error(result.error || 'SetValue failed');
                }
                break;
            }

            case 'indexeddb': {
                // Map store names to IndexedDB object stores
                const idbStoreName = this.mapStoreNameToIDB(storeName);
                const tx = this.db.transaction(idbStoreName, 'readwrite');
                const store = tx.objectStore(idbStoreName);
                await store.put(value, key);
                await tx.done;
                break;
            }
        }
    }

    /**
     * Initialize IndexedDB database and object stores
     */
    async initIndexedDB() {
        try {
            const { openDB } = await import('idb');
            // v3: outbox_sales keys are store_db + NUL + sale_id
            this.db = await openDB('pos-db', 3, {
                upgrade(db) {
                    if (!db.objectStoreNames.contains('main')) db.createObjectStore('main');
                    if (!db.objectStoreNames.contains('adminUsers')) db.createObjectStore('adminUsers');
                    if (!db.objectStoreNames.contains('sessions')) db.createObjectStore('sessions');
                    if (!db.objectStoreNames.contains('outbox_sales')) db.createObjectStore('outbox_sales');
                    if (!db.objectStoreNames.contains('outbox_voids')) db.createObjectStore('outbox_voids');
                    if (!db.objectStoreNames.contains('outbox_logins')) db.createObjectStore('outbox_logins');
                    if (!db.objectStoreNames.contains('invoice_number')) db.createObjectStore('invoice_number');
                }
            });
            await this.migrateIdbOutboxCompositeKeys();
        } catch (e) {
            console.error('[StorageAdapter] Failed to initialize IndexedDB:', e);
            throw e;
        }
    }

    outboxIdbKey(saleId, storeDb) {
        return `${storeDb || ''}\0${saleId || ''}`;
    }

    async migrateIdbOutboxCompositeKeys() {
        if (!this.db || !this.db.objectStoreNames.contains('outbox_sales')) return;
        try {
            const tx = this.db.transaction('outbox_sales', 'readwrite');
            const store = tx.store;
            const keys = await store.getAllKeys();
            const values = await store.getAll();
            for (let i = 0; i < keys.length; i++) {
                const key = keys[i];
                const row = values[i];
                if (typeof key === 'string' && key.includes('\0')) continue;
                const saleId = row?.sale_id || key;
                const storeDb = row?.store_db != null ? String(row.store_db) : '';
                const newKey = this.outboxIdbKey(saleId, storeDb);
                const nextRow = {
                    ...(row && typeof row === 'object' ? row : {}),
                    sale_id: saleId,
                    store_db: storeDb
                };
                await store.delete(key);
                await store.put(nextRow, newKey);
            }
            await tx.done;
        } catch (e) {
            console.warn('[StorageAdapter] IDB outbox key migration skipped/failed:', e);
        }
    }

    /**
     * Map SQL table names to IndexedDB store names
     * @param {string} tableName SQL table name
     * @returns {string} IndexedDB store name
     */
    mapStoreNameToIDB(tableName) {
        const mapping = {
            'main_store': 'main',
            'admin_users': 'adminUsers',
            'sessions': 'sessions',
            'outbox_sales': 'outbox_sales',
            'outbox_voids': 'outbox_voids',
            'outbox_logins': 'outbox_logins',
            'invoice_numbers': 'invoice_number'
        };
        return mapping[tableName] || tableName;
    }

    /**
     * Execute IndexedDB operations (fallback for development)
     * This is a simplified mapper for basic operations
     * @param {string} sql SQL statement
     * @param {Array} params Parameters
     * @returns {Promise<any>} Result
     */
    async executeIndexedDB(sql, params = []) {
        const sqlLower = sql.toLowerCase().trim();

        let tableName;
        if (sqlLower.includes('from ')) {
            tableName = sql.match(/from\s+(\w+)/i)?.[1];
        } else if (sqlLower.includes('into ')) {
            tableName = sql.match(/into\s+(\w+)/i)?.[1];
        } else if (sqlLower.includes('update ')) {
            tableName = sql.match(/update\s+(\w+)/i)?.[1];
        } else if (sqlLower.startsWith('delete from ')) {
            tableName = sql.match(/delete\s+from\s+(\w+)/i)?.[1];
        }

        if (!tableName) {
            console.warn('[StorageAdapter] Could not parse table name from SQL:', sql);
            return [];
        }

        const idbStoreName = this.mapStoreNameToIDB(tableName);
        const tx = this.db.transaction(idbStoreName, sqlLower.startsWith('select') ? 'readonly' : 'readwrite');
        const store = tx.objectStore(idbStoreName);

        if (sqlLower.startsWith('select')) {
            if (sqlLower.includes('where key = ?')) {
                const row = await store.get(params[0]);
                if (!row) return undefined;
                if (sqlLower.includes('select value')) {
                    return { value: row.value !== undefined ? row.value : row };
                }
                return row;
            }

            if (tableName === 'outbox_sales' && sqlLower.includes('where sale_id = ? and store_db = ?')) {
                const row = await store.get(this.outboxIdbKey(params[0], params[1]));
                if (!row) return undefined;
                if (sqlLower.includes('select attempts')) return { attempts: row.attempts || 0 };
                return row;
            }

            if (tableName === 'outbox_sales' && sqlLower.includes('where sale_id = ?')) {
                const all = await store.getAll();
                const row = all.find((r) => r?.sale_id === params[0]);
                if (!row) return undefined;
                if (sqlLower.includes('select attempts')) return { attempts: row.attempts || 0 };
                return row;
            }

            let rows = await store.getAll();

            if (tableName === 'outbox_sales' && sqlLower.includes('where store_db = ?') && !sqlLower.includes('sale_id')) {
                rows = rows.filter((r) => String(r?.store_db || '') === String(params[0] || ''));
            }

            if (sqlLower.includes("where status in ('pending', 'timeout')")) {
                rows = rows.filter((r) => r?.status === 'pending' || r?.status === 'timeout');
            }

            if (sqlLower.includes("where status in ('pending', 'timeout', 'failed', 'sending')")) {
                rows = rows.filter((r) =>
                    r?.status === 'pending' || r?.status === 'timeout' || r?.status === 'failed' || r?.status === 'sending'
                );
            }

            if (sqlLower.includes('where status = ?')) {
                rows = rows.filter((r) => r?.status === params[0]);
            }

            if (sqlLower.includes('order by date desc')) {
                rows = [...rows].sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0));
            } else if (sqlLower.includes('order by date asc')) {
                rows = [...rows].sort((a, b) => new Date(a?.date || 0) - new Date(b?.date || 0));
            }

            if (sqlLower.includes('limit ?')) {
                const limit = Number(params[params.length - 1]);
                if (Number.isFinite(limit) && limit > 0) rows = rows.slice(0, limit);
            }

            if (sqlLower.includes('count(*)')) {
                return { count: rows.length };
            }

            return rows;
        }

        if (sqlLower.startsWith('insert')) {
            if (tableName === 'outbox_sales' && params.length >= 11) {
                const row = {
                    sale_id: params[0],
                    date: params[1],
                    status: params[2],
                    upload_response: params[3],
                    upload_date: params[4],
                    payload: params[5],
                    upload_invoice: params[6],
                    attempts: params[7],
                    last_attempt_date: params[8],
                    store_db: params[9] != null ? String(params[9]) : '',
                    client_meta: params[10]
                };
                const key = this.outboxIdbKey(row.sale_id, row.store_db);
                const existing = await store.get(key);
                if (existing) {
                    throw new Error('UNIQUE constraint failed: outbox_sales.sale_id');
                }
                await store.put(row, key);
                await tx.done;
                return { changes: 1, lastInsertRowid: row.sale_id };
            }

            if (sqlLower.includes('on conflict(key) do update')) {
                const key = params[0];
                const value = params[1];
                const existing = await store.get(key);
                const row = {
                    ...(existing && typeof existing === 'object' ? existing : {}),
                    key,
                    value,
                    updated_at: new Date().toISOString()
                };
                await store.put(row, key);
                await tx.done;
                return { changes: 1, lastInsertRowid: key };
            }

            await store.put(params[0]);
            await tx.done;
            return { changes: 1 };
        }

        if (sqlLower.startsWith('update')) {
            if (tableName === 'outbox_sales') {
                let row;
                let idbKey;
                if (sqlLower.includes('where sale_id = ? and store_db = ?')) {
                    const saleId = params[params.length - 2];
                    const storeDb = params[params.length - 1];
                    idbKey = this.outboxIdbKey(saleId, storeDb);
                    row = await store.get(idbKey);
                } else {
                    const saleId = params[params.length - 1];
                    const all = await store.getAll();
                    row = all.find((r) => r?.sale_id === saleId);
                    if (row) idbKey = this.outboxIdbKey(row.sale_id, row.store_db);
                }
                if (!row || !idbKey) return { changes: 0 };

                if (sqlLower.includes("set status = 'sending'")) {
                    row.status = 'sending';
                    row.attempts = Number(row.attempts || 0) + 1;
                    row.last_attempt_date = params[0];
                } else if (sqlLower.includes("set status = 'sent'")) {
                    row.status = 'sent';
                    row.upload_response = params[0];
                    row.upload_date = params[1];
                    row.upload_invoice = params[2];
                } else if (sqlLower.includes('set status = ?') && sqlLower.includes('upload_response = ?')) {
                    row.status = params[0];
                    row.upload_response = params[1];
                    row.last_attempt_date = params[2];
                } else if (sqlLower.includes('set attempts = attempts + 1') && sqlLower.includes("status = 'pending'")) {
                    row.attempts = Number(row.attempts || 0) + 1;
                    row.status = 'pending';
                    row.last_attempt_date = params[0];
                } else if (sqlLower.includes('set payload = ?') && sqlLower.includes("status = 'pending'") && sqlLower.includes('attempts = 0')) {
                    row.payload = params[0];
                    if (sqlLower.includes('client_meta = ?')) {
                        row.client_meta = params[1];
                    }
                    row.status = 'pending';
                    row.attempts = 0;
                    row.upload_response = null;
                    row.upload_date = null;
                } else {
                    return { changes: 0 };
                }

                row.updated_at = new Date().toISOString();
                await store.put(row, idbKey);
                await tx.done;
                return { changes: 1 };
            }
            return { changes: 0 };
        }

        if (sqlLower.startsWith('delete')) {
            if (tableName === 'outbox_sales' && sqlLower.includes('where sale_id = ? and store_db = ?')) {
                await store.delete(this.outboxIdbKey(params[0], params[1]));
                await tx.done;
                return { changes: 1 };
            }
            if (tableName === 'outbox_sales' && sqlLower.includes('where sale_id = ?')) {
                const all = await store.getAll();
                const keys = await store.getAllKeys();
                let changes = 0;
                for (let i = 0; i < all.length; i++) {
                    if (all[i]?.sale_id === params[0]) {
                        await store.delete(keys[i]);
                        changes += 1;
                    }
                }
                await tx.done;
                return { changes };
            }
            if (tableName === 'outbox_sales' && sqlLower.includes("where status = 'sent' and date < ?")) {
                const threshold = params[0];
                const rows = await store.getAll();
                const keys = await store.getAllKeys();
                let changes = 0;
                for (let i = 0; i < rows.length; i++) {
                    if (rows[i]?.status === 'sent' && rows[i]?.date && rows[i].date < threshold) {
                        await store.delete(keys[i]);
                        changes += 1;
                    }
                }
                await tx.done;
                return { changes };
            }
            if (params[0]) {
                await store.delete(params[0]);
                await tx.done;
                return { changes: 1 };
            }
            return { changes: 0 };
        }

        return [];
    }
}

// Singleton instance
let storageInstance = null;

/**
 * Get storage adapter instance
 * @returns {Promise<StorageAdapter>} Storage adapter
 */
export async function getStorage() {
    if (!storageInstance) {
        storageInstance = new StorageAdapter();
    }
    await storageInstance.init();
    return storageInstance;
}

/**
 * Helper function for simple key-value operations
 * @param {string} table Table/store name
 * @param {string} key Key
 * @param {any} value Value (optional, if omitted will GET)
 * @returns {Promise<any>}
 */
export async function kvStore(table, key, value = undefined) {
    const storage = await getStorage();

    if (value === undefined) {
        // GET operation
        const row = await storage.get(
            `SELECT value FROM ${table} WHERE key = ? `,
            [key]
        );
        return row ? JSON.parse(row.value) : undefined;
    } else {
        // SET operation
        await storage.run(
            `INSERT INTO ${table} (key, value, updated_at)
VALUES(?, ?, CURRENT_TIMESTAMP)
             ON CONFLICT(key) DO UPDATE SET value = ?, updated_at = CURRENT_TIMESTAMP`,
            [key, JSON.stringify(value), JSON.stringify(value)]
        );
    }
}

/**
 * Get value by key (Storage.js compatibility)
 * @param {string} key Key to retrieve
 * @param {string} storeName Store name (main_store, admin_users, sessions)
 * @returns {Promise<any>} Value or undefined
 */
export async function getValue(key, storeName = 'main_store') {
    const storage = await getStorage();
    return storage.getValue(key, storeName);
}

/**
 * Set value by key (Storage.js compatibility)
 * @param {string} key Key to set
 * @param {any} value Value to store
 * @param {string} storeName Store name (main_store, admin_users, sessions)
 * @returns {Promise<void>}
 */
export async function setValue(key, value, storeName = 'main_store') {
    const storage = await getStorage();
    return storage.setValue(key, value, storeName);
}

export default {
    getStorage,
    kvStore,
    getValue,
    setValue
};
